import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { Dialog } from '@material/mwc-dialog';
import { GetTopicResponse } from '../types';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { detectMobileViewport } from '../helpers/device';
import { getQueryStringParameters } from '../helpers/urls';
import topicData from '../helpers/state/topic-data';
import { getMainPage } from '../helpers/page-name';

@customElement('bookmark-map-button')
export class BookmarkMapButton extends LitElement {
    amplitude_callsite: string = 'Bookmark Map';



    @query('#signinModalComponent')
    signinModalComponent!: Dialog;

    @query('#bookmarkModal')
    bookmarkModal!: Dialog;

    @property({ type: Object })
    topic?: GetTopicResponse;

    @property({ type: Boolean })
    onWhite: Boolean = false;

    @property({ type: String })
    variant: String = 'icon';

    bookmarkAfterSignin: boolean = false;

    @state()
    isBookmarked = false;

    @state()
    mobilePreview: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindTopicState = new StateController(this, topicData);

    async firstUpdated() {
        this.isBookmarked = accountInfo.bookmarks.some((b) => b.topic_id.$oid === this.topic?._id.$oid) ?? false;
    }

    private async bookmarkMap() {
        if (this.topic === undefined) {
            // The topic cannot be bookmarked: it is invalid.
            getMainPage().errorToastElement.showToastAndThrow('Rats. The topic cannot be bookmarked.');
            return;
        }

        if (this.isBookmarked) {
            const queryString = getQueryStringParameters();
            const topic_key = queryString.get('topic_key') ?? topicData.topicKey;

            await accountInfo.unbookmarkTopic(this.topic._id.$oid, topic_key);

            this.isBookmarked = false;
        } else {
            if (!accountInfo.isAuthenticated) {
                this.bookmarkAfterSignin = true;
                this.signinModalComponent.open = true;
            } else {
                const queryString = getQueryStringParameters();
                const topic_key = queryString.get('topic_key') ?? topicData.topicKey;

                if (this.topic.discovery_level === 'private') {
                    if (topic_key === undefined) {
                        // Unable to fetch topic key.
                        getMainPage().errorToastElement.showToastAndThrow(
                            'Sorry, we are missing some info and can not bookmark this right now.'
                        );
                    }
                }

                await accountInfo.bookmarkTopic(
                    this.topic._id.$oid,
                    topic_key
                );

                this.isBookmarked = true;
                this.bookmarkModal.open = true;
            }
        }
    }

    protected render() {
        return html`
            ${this.variant === 'icon' ? html`
                <mwc-icon
                    class=${this.onWhite === true ? 'bookmark--black' : 'bookmark'}
                    style="cursor: pointer;"
                    @click="${this.bookmarkMap}">
                    ${this.isBookmarked ? 'bookmark' : 'bookmark_border'}
                </mwc-icon>
            ` : ''}
            ${this.variant === 'horizontal' ? html`
                <form-button variant="outline" size="sm" class="inner-btn" ?vertical=${detectMobileViewport(this.mobilePreview)} ?fullWidth=${true} @click="${this.bookmarkMap}">
                    ${this.isBookmarked ? html`
                        <mwc-icon>bookmark</mwc-icon>
                    ` : html`
                        <mwc-icon>bookmark_border</mwc-icon>
                    `}
                    <translated-span style="margin-left: 0.25rem">${this.isBookmarked ? 'Bookmarked' : 'Bookmark'}</translated-span>
                </form-button>
            ` : ''}
            ${this.variant === 'vertical' ? html`
                <form-button variant="outline" size="sm" class="inner-btn" ?fullWidth=${true} ?vertical=${true} @click="${this.bookmarkMap}">
                    ${this.isBookmarked ? html`
                        <mwc-icon>bookmark</mwc-icon>
                    ` : html`
                        <mwc-icon>bookmark_border</mwc-icon>
                    `}
                    <translated-span style="margin-top: 0.5rem">Bookmark</translated-span>
                </form-button>
            ` : ''}
            <error-toast id="errorToast"></error-toast>

            <mwc-dialog id="signinModalComponent" class="dialog" hideActions>
                <modal-dialog size="md" @close="${() => this.signinModalComponent.close()}">
                    <signin-modal-component
                        slot="body"
                        ?has_pending_bookmark=${this.bookmarkAfterSignin}
                        @close="${() => this.signinModalComponent.close()}">
                    </signin-modal-component>
                </modal-dialog>
            </mwc-dialog>

            <mwc-dialog id="bookmarkModal" class="dialog" hideActions>
                <modal-dialog size="lg" @close="${() => this.bookmarkModal.close()}">
                    <div slot="body" class="bookmarkModalBody">
                        <bookmark-map-modal .topicName=${this.topic?.name}></bookmark-map-modal>
                    </div>
                </modal-dialog>
            </mwc-dialog>
        `;
    }

    static get styles() {
        return css`
            .inner-btn {
                --mdc-icon-size: 1rem;
            }
        `;
    }
}
